import {
  getAlertsSummary,
  getWebhookFailureCounts,
  type AlertsSummaryResponse,
  type WebhookFailureCountsResponse,
} from '../api/endpoints.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  PageHead,
  StatCard,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';
import { RuleNameBreaks } from './user-inspect/SoftBreaks.js';
import '../styles/activity.mobile.css';

export function AlertsPage(): JSX.Element {
  const canView = useCan('observability:read');
  const alertsQuery = useApiQuery<AlertsSummaryResponse>(
    (options) => getAlertsSummary(options, 'mailcom'),
    [],
  );
  const countsQuery = useApiQuery<WebhookFailureCountsResponse>(
    (options) => getWebhookFailureCounts(options),
    [],
  );
  if (!canView) {
    return (
      <div className="alerts-page">
        <PageHead title="Uyarılar ve Sayaçlar" />
        <ErrorState reason="capability_missing:observability:read" />
      </div>
    );
  }
  return (
    <div className="alerts-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Uyarılar</>}
        title="Uyarılar ve Sayaçlar"
        subtitle={
          <>
            Observability köprüsünden gelen canlı uyarılar ve webhook hata sayaçları.
            Bağlanmamış uyarı arka uçları sessizce yeşil değil, <code>unknown</code>{' '}
            döner; bağlanmamış sayaçlar sıfır gösterir.
          </>
        }
      />

      <Card flush>
        <CardHeader title="Uyarı özeti" />
        <CardBody>
          {alertsQuery.kind === 'loading' ? <LoadingState /> : null}
          {alertsQuery.kind === 'error' ? (
            <ErrorState reason={alertsQuery.reason} onRetry={alertsQuery.reload} />
          ) : null}
          {alertsQuery.kind === 'ok' ? <AlertSummary data={alertsQuery.data.summary} /> : null}
        </CardBody>
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card flush>
          <CardHeader title="Payment webhook failures" />
          <CardBody>
            {countsQuery.kind === 'loading' ? <LoadingState /> : null}
            {countsQuery.kind === 'error' ? (
              <ErrorState reason={countsQuery.reason} onRetry={countsQuery.reload} />
            ) : null}
            {countsQuery.kind === 'ok' ? <WebhookCounters data={countsQuery.data.counts} /> : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function severityTone(s: string): BadgeTone {
  switch (s) {
    case 'ok': return 'success';
    case 'warning': return 'warn';
    case 'critical':
    case 'page': return 'danger';
    case 'info': return 'info';
    default: return 'neutral';
  }
}

/**
 * Presentation only: a counter with more than 10 digits does not fit a
 * 2-up phone KPI card on one line, so that grid goes 1-up on phones
 * (`.kpi-grid-single`, styles.css §10) instead of breaking the number
 * inside a digit group. A counter with more than 12 digits (out of
 * contract) also gets wider cards on tablets and desktops (`.kpi-grid-wide`,
 * styles/activity.mobile.css); realistic values leave those grids unchanged.
 */
function kpiGridClass(values: readonly string[]): string {
  const digits = Math.max(0, ...values.map((v) => v.replace(/\D/g, '').length));
  if (digits > 12) return 'kpi-grid kpi-grid-single kpi-grid-wide';
  return digits > 10 ? 'kpi-grid kpi-grid-single' : 'kpi-grid';
}

function AlertSummary({ data }: { readonly data: AlertsSummaryResponse['summary'] }): JSX.Element {
  const counts = [
    data.firingCount,
    data.criticalCount,
    data.pageCount,
    data.warningCount,
    data.okCount,
    data.unknownCount,
  ].map((n) => String(n));
  return (
    <div>
      {/* styles/activity.mobile.css: wraps, so a long severity drops under
          the label instead of crushing it. */}
      <div className="alert-overall">
        <span className="t-muted">Genel ciddiyet:</span>
        <Badge tone={severityTone(data.overallSeverity)} dot>{data.overallSeverity}</Badge>
      </div>
      <div className={kpiGridClass(counts)}>
        <StatCard label="Firing" value={String(data.firingCount)} testId="al-firing" />
        <StatCard label="Critical" value={String(data.criticalCount)} testId="al-critical" />
        <StatCard label="Page" value={String(data.pageCount)} testId="al-page" />
        <StatCard label="Warning" value={String(data.warningCount)} testId="al-warning" />
        <StatCard label="OK" value={String(data.okCount)} testId="al-ok" />
        <StatCard label="Unknown" value={String(data.unknownCount)} testId="al-unknown" />
      </div>
      <h3 style={{ margin: '20px 0 12px', fontSize: 14, fontWeight: 600 }}>Son kritikler</h3>
      {data.recentCritical.length === 0 ? (
        <EmptyState label="Firing kritik uyarı yok." icon={<Icon.CheckCircle size={22} />} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.recentCritical.map((a) => (
            <div
              key={a.alertId}
              style={{
                padding: '10px 12px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-elevated)',
              }}
            >
              {/* Head row (styles/activity.mobile.css): the state + fired-at
                  group never breaks apart; on phones it drops under the
                  badge + title line. */}
              <div className="alert-crit-head">
                <Badge tone={severityTone(a.severity)} dot>{a.severity}</Badge>
                <strong className="alert-crit-title"><RuleNameBreaks text={a.title} /></strong>
                <span className="alert-crit-meta">
                  <span className="t-tertiary t-small">/ {a.state}</span>
                  {a.firedAt ? <span className="t-tertiary t-xs t-mono">{a.firedAt}</span> : null}
                </span>
              </div>
              {a.detail ? <div className="t-small t-tertiary alert-crit-detail" style={{ marginTop: 4 }}>{a.detail}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WebhookCounters({ data }: { readonly data: WebhookFailureCountsResponse['counts'] }): JSX.Element {
  const counts = [
    data.applied,
    data.signatureRejected,
    data.malformed,
    data.duplicate,
    data.intentUnknown,
    data.failureTotal,
  ].map((n) => n.toLocaleString('tr-TR'));
  return (
    <div>
      <div className={kpiGridClass(counts)}>
        <StatCard label="Applied" value={data.applied.toLocaleString('tr-TR')} testId="wh-applied" />
        <StatCard label="Signature rejected" value={data.signatureRejected.toLocaleString('tr-TR')} testId="wh-sig" />
        <StatCard label="Malformed" value={data.malformed.toLocaleString('tr-TR')} testId="wh-mal" />
        <StatCard label="Duplicate (replay)" value={data.duplicate.toLocaleString('tr-TR')} testId="wh-dup" />
        <StatCard label="Intent unknown" value={data.intentUnknown.toLocaleString('tr-TR')} testId="wh-unknown" />
        <StatCard
          label="Failure total"
          value={data.failureTotal.toLocaleString('tr-TR')}
          trend={data.failureTotal > 0 ? 'down' : 'flat'}
          testId="wh-failure-total"
        />
      </div>
      <div className="t-tertiary t-small alert-since" style={{ marginTop: 12, fontFamily: 'var(--font-mono)' }}>
        since: {data.since}
      </div>
    </div>
  );
}
